/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './FinancialServicesHero.module.scss';

const ENTITIES = [
  { name: 'ACME Inc.', flag: '🇺🇸', registry: 'Delaware SoS', updated: '19 Jul 2026' },
  { name: 'ACME UK Ltd', flag: '🇬🇧', registry: 'Companies House', updated: '19 Jul 2026' },
  { name: 'ACME Canada Inc.', flag: '🇨🇦', registry: 'Corporations Canada', updated: '18 Jul 2026' },
  { name: 'ACME France SAS', flag: '🇫🇷', registry: 'Infogreffe (RCS)', updated: '17 Jul 2026' },
  { name: 'ACME GmbH', flag: '🇩🇪', registry: 'Handelsregister', updated: '16 Jul 2026' },
  { name: 'ACME Shanghai Co.', flag: '🇨🇳', registry: 'SAMR · GSXT', updated: '15 Jul 2026' },
];

export const FinancialServicesHero = () => {
  const { t } = useInt();
  const panelRef = useRef<HTMLDivElement>(null);
  const started = useIntersection(panelRef, { threshold: 0.3 });
  const [revealed, setRevealed] = useState(0);

  useEffect(() => {
    if (!started) {
      return undefined;
    }

    const timers = ENTITIES.map((_, i) =>
      setTimeout(() => setRevealed((count) => Math.max(count, i + 1)), 260 + i * 260),
    );

    return () => timers.forEach(clearTimeout);
  }, [started]);

  const done = revealed >= ENTITIES.length;

  return (
    <Section className={styles.root}>
      <div className={styles.grid}>
        <div className={styles.copy}>
          <TitleBlock
            size="page"
            align="left"
            title={t('Company intelligence built for regulated decisions.')}
            description={
              <>
                {t(
                  'From KYB and onboarding to credit risk and ongoing monitoring, Global Database gives financial institutions',
                )}{' '}
                <b>{t('traceable company data grounded in official registry records')}</b>.
              </>
            }
          />
          <div className={styles.actions}>
            <Button type="blue" size="xxl" href="/free-trial">
              {t('Start a free trial')}
            </Button>
            <Button type="dark-outline" size="xxl" href="/data-sources">
              {t('View data sources')}
            </Button>
          </div>
        </div>

        <div className={styles.panel} ref={panelRef}>
          <div className={styles.panelTop}>
            <div className={styles.avatar}>AG</div>
            <div className={styles.co}>
              <span className={styles.coName}>ACME Group</span>
              <span className={styles.coMeta}>{t('Corporate group · 6 jurisdictions')}</span>
            </div>
            <span className={cn(styles.badge, done ? styles.badgeActive : styles.badgeSourcing)}>
              {done ? t('Active') : t('Sourcing')}
            </span>
          </div>

          <div className={styles.colHead}>
            <span>{t('Entity')}</span>
            <span>{t('Source')}</span>
            <span>{t('Last update')}</span>
          </div>

          {ENTITIES.map((entity, i) => (
            <div key={entity.name} className={cn(styles.row, i < revealed && styles.rowIn)}>
              <span className={styles.entityName}>{entity.name}</span>
              <span className={styles.source}>
                <span className={styles.flag}>{entity.flag}</span>
                {entity.registry}
              </span>
              <span className={styles.updated}>{entity.updated}</span>
            </div>
          ))}

          <div className={cn(styles.foot, done && styles.footIn)}>
            <span>{t('6 entities · sourced from 6 government registries')}</span>
            <span>globaldatabase.com/group/acme</span>
          </div>

          <div className={styles.progress}>
            <i style={{ width: `${(revealed / ENTITIES.length) * 100}%` }} />
          </div>
        </div>
      </div>
    </Section>
  );
};
